import { useEffect, useState } from 'react';

/**
 * Hook partagé : résout les variables CSS du thème Synolog en strings
 * hex/rgba (les `<svg>` maison et certaines libs ne savent pas lire les
 * `var(--x)` dans des props calculées). Recalcule au mount + quand la classe
 * de thème (.light / .dark) change sur <html> (MutationObserver).
 *
 * NB : `AuditView` conserve sa propre copie historique (Recharts) ; ce module
 * est la version réutilisable pour les charts SVG maison.
 */

const COLOR_FALLBACKS = {
  '--accent': '#326496',
  '--primary': '#2a3147',
  '--success': '#22c55e',
  '--warning': '#f59e0b',
  '--danger': '#ef4444',
  '--text': '#f1f5f9',
  '--text-muted': '#94a3b8',
  '--glass-border': 'rgba(255,255,255,0.1)',
  '--card-bg': '#1e293b',
  '--bg': '#0f172a',
};

export function resolveThemeColors() {
  const cs =
    typeof window !== 'undefined' && document?.documentElement
      ? getComputedStyle(document.documentElement)
      : null;
  const read = (name) => {
    const v = cs ? cs.getPropertyValue(name).trim() : '';
    return v || COLOR_FALLBACKS[name];
  };
  return {
    accent: read('--accent'),
    primary: read('--primary'),
    success: read('--success'),
    warning: read('--warning'),
    danger: read('--danger'),
    text: read('--text'),
    textMuted: read('--text-muted'),
    glassBorder: read('--glass-border'),
    cardBg: read('--card-bg'),
    bg: read('--bg'),
  };
}

export function useThemeColors() {
  const [colors, setColors] = useState(resolveThemeColors);
  useEffect(() => {
    const update = () => setColors(resolveThemeColors());
    update();
    const html = document.documentElement;
    const obs = new MutationObserver(update);
    obs.observe(html, { attributes: true, attributeFilter: ['class', 'style'] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

/** Palette catégorielle dérivée des couleurs du thème (lisible light + dark). */
export function buildCategoricalPalette(c) {
  return [
    c.accent,
    c.primary,
    c.success,
    c.warning,
    c.danger,
    '#8b5cf6',
    '#06b6d4',
    '#ec4899',
    '#84cc16',
    '#f97316',
  ];
}

export default useThemeColors;
